// Customer booking wizard: service → barber → date → time → review → done. Also used for rescheduling.
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/client.ts';
import type { Booking, DateAvailability, TimeSlot } from '../api/types.ts';
import { useShop } from '../app/boot.tsx';
import { CustomerTabs, useShopPaths } from '../app/nav.tsx';
import { message } from '../lib/errors.ts';
import { dayParts, thaiDate, thaiDateTime, timeOf } from '../lib/time.ts';
import { Banner, Button, Card, Empty, Field, Loading, Page } from '../ui/index.tsx';

type Step = 'service' | 'barber' | 'date' | 'time' | 'review' | 'done';
const ORDER: Step[] = ['service', 'barber', 'date', 'time', 'review'];

const TIME_ERRORS = new Set(['SLOT_TAKEN', 'TIME_TOO_SOON', 'TIME_INVALID', 'OUTSIDE_WINDOW', 'CUSTOMER_OVERLAP']);

function initials(name: string) {
  return [...name.trim()].slice(0, 2).join('');
}

export function Book({ reschedule }: { reschedule?: { id: string; serviceId: string } }) {
  const { shop, me, api, liff } = useShop();
  const { go } = useShopPaths();
  const [step, setStep] = useState<Step>(reschedule ? 'barber' : 'service');
  const [serviceId, setServiceId] = useState<string | null>(reschedule?.serviceId ?? null);
  const [barberId, setBarberId] = useState<string | null | undefined>(undefined);
  const [dates, setDates] = useState<DateAvailability[] | null>(null);
  const [date, setDate] = useState<string | null>(null);
  const [times, setTimes] = useState<TimeSlot[] | null>(null);
  const [slot, setSlot] = useState<TimeSlot | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<{ text: string; action?: 'my' | 'call' } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<Booking | null>(null);
  const keyRef = useRef<string | null>(null);
  const submittingRef = useRef(false);

  const service = shop.services.find((s) => s.id === serviceId) ?? null;
  const barbersForService = service ? shop.barbers.filter((b) => service.barberIds.includes(b.id)) : [];
  const barberName = (id: string | null | undefined) => shop.barbers.find((b) => b.id === id)?.name ?? '';
  const registered = me.customer?.registered === true;

  useEffect(() => {
    if (!registered) go(`/register?next=${encodeURIComponent('/book')}`, { replace: true });
  }, [registered, go]);

  const loadDates = useCallback(async (svc: string, barber: string | null) => {
    setDates(null);
    try {
      setDates(await api.availableDates({ serviceId: svc, barberId: barber }));
    } catch (e) {
      setError({ text: message(e) });
      setDates([]);
    }
  }, [api]);

  const loadTimes = useCallback(async (svc: string, barber: string | null, d: string) => {
    setTimes(null);
    try {
      setTimes(await api.availableTimes({ serviceId: svc, barberId: barber, date: d, mode: 'customer' }));
    } catch (e) {
      setError({ text: message(e) });
      setTimes([]);
    }
  }, [api]);

  if (!registered) return <Loading />;

  const call = shop.phone ? { label: '📞 โทรหาร้าน', href: `tel:${shop.phone}` } : undefined;
  if (!me.shop.bookingOpen) {
    return <Page title="จองคิว"><Banner kind="warn" action={call}>ระบบจองปิดชั่วคราว กรุณาติดต่อร้าน</Banner><CustomerTabs /></Page>;
  }
  if (me.customer?.blocked) {
    return <Page title="จองคิว"><Banner kind="error" action={call}>บัญชีถูกระงับการจองออนไลน์ กรุณาติดต่อร้าน</Banner><CustomerTabs /></Page>;
  }
  if (shop.services.length === 0) {
    return <Page title="จองคิว"><Empty title="ยังไม่มีบริการที่เปิดให้จอง" action={call} /><CustomerTabs /></Page>;
  }

  const back = () => {
    setError(null);
    const i = ORDER.indexOf(step);
    const first = reschedule ? 1 : 0;
    if (i > first) setStep(ORDER[i - 1]);
    else if (reschedule) go('/my');
  };

  const chooseService = (id: string) => { setServiceId(id); setBarberId(undefined); setStep('barber'); setError(null); };
  const chooseBarber = (id: string | null) => {
    setBarberId(id); setDate(null); setSlot(null); setStep('date'); setError(null);
    void loadDates(serviceId as string, id);
  };
  const chooseDate = (d: string) => {
    setDate(d); setSlot(null); setStep('time'); setError(null);
    void loadTimes(serviceId as string, barberId ?? null, d);
  };
  const chooseTime = (s: TimeSlot) => { setSlot(s); setStep('review'); setError(null); };

  async function submit() {
    if (submittingRef.current || !slot || !serviceId) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    keyRef.current ??= crypto.randomUUID();
    try {
      const b = reschedule
        ? await api.reschedule(reschedule.id, { barberId: barberId ?? null, startAt: slot.start, idempotencyKey: keyRef.current })
        : await api.createBooking({ serviceId, barberId: barberId ?? null, startAt: slot.start, note: note.trim() || undefined, idempotencyKey: keyRef.current });
      setResult(b);
      keyRef.current = null;
      setStep('done');
    } catch (e) {
      const code = e instanceof ApiError ? e.code : '';
      if (TIME_ERRORS.has(code)) {
        setError({ text: message(e), action: code === 'CUSTOMER_OVERLAP' ? 'my' : undefined });
        setSlot(null);
        setStep('time');
        void loadTimes(serviceId, barberId ?? null, date as string);
      } else if (code === 'BARBER_UNAVAILABLE' || code === 'SERVICE_UNAVAILABLE') {
        setError({ text: message(e) });
        setStep(code === 'BARBER_UNAVAILABLE' ? 'barber' : 'service');
      } else if (code === 'MAX_UPCOMING') {
        setError({ text: message(e), action: 'my' });
      } else if (code === 'CUSTOMER_BLOCKED' || code === 'BOOKING_CLOSED') {
        setError({ text: message(e), action: 'call' });
      } else {
        setError({ text: message(e) });
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  const errorBanner = error && (
    <Banner kind="error" action={error.action === 'my' ? { label: 'การจองของฉัน', onClick: () => go('/my') } : error.action === 'call' ? call : undefined}>
      {error.text}
    </Banner>
  );

  if (step === 'done' && result) {
    const title = reschedule ? 'เลื่อนนัดสำเร็จ' : 'จองคิวสำเร็จ';
    return (
      <Page title={reschedule ? 'เลื่อนนัด' : 'จองคิว'}>
        <div className="empty" style={{ paddingTop: 16 }}>
          <div className="empty-mark" aria-hidden="true">✓</div>
          <p className="empty-title">{title}</p>
        </div>
        <Card>
          <dl className="summary">
            <dt>บริการ</dt><dd>{result.serviceName}</dd>
            <dt>ช่าง</dt><dd>{barberName(result.barberId) || '-'}</dd>
            <dt>วันเวลา</dt><dd>{thaiDateTime(result.startAt)}</dd>
          </dl>
        </Card>
        <div className="stack" style={{ marginTop: 16 }}>
          <Button block onClick={() => go('/my')}>ดูการจอง</Button>
          {liff.isInClient() && <Button block variant="secondary" onClick={() => liff.closeWindow()}>ปิด</Button>}
        </div>
      </Page>
    );
  }

  const stepIndex = ORDER.indexOf(step);
  const title = reschedule ? 'เลื่อนนัด' : 'จองคิว';
  return (
    <>
      <Page title={title} back={stepIndex > (reschedule ? 1 : 0) || reschedule ? back : undefined}>
        <div className="steps" aria-hidden="true">
          {ORDER.slice(reschedule ? 1 : 0).map((s) => <span key={s} className={ORDER.indexOf(s) <= stepIndex ? 'done' : ''} />)}
        </div>
        {errorBanner}

        {step === 'service' && (
          <section className="stack" aria-label="เลือกบริการ">
            <h2>เลือกบริการ</h2>
            {shop.services.map((s) => (
              <Card key={s.id} as="button" onClick={() => chooseService(s.id)} className={s.id === serviceId ? 'selected' : ''}>
                <div className="row-between">
                  <div>
                    <div className="card-title">{s.name}</div>
                    <div className="muted small">{s.durationMin} นาที</div>
                  </div>
                  <span className="price">{s.price.toLocaleString('th-TH')} บาท</span>
                </div>
              </Card>
            ))}
          </section>
        )}

        {step === 'barber' && service && (
          <section className="stack" aria-label="เลือกช่าง">
            <h2>เลือกช่าง</h2>
            <p className="muted small">{service.name} · {service.durationMin} นาที</p>
            {barbersForService.length > 1 && (
              <Card as="button" onClick={() => chooseBarber(null)} className={barberId === null ? 'selected' : ''}>
                <div className="row"><span className="avatar" aria-hidden="true">✦</span><div className="card-title">ช่างคนไหนก็ได้</div></div>
              </Card>
            )}
            {barbersForService.map((b) => (
              <Card key={b.id} as="button" onClick={() => chooseBarber(b.id)} className={b.id === barberId ? 'selected' : ''}>
                <div className="row">
                  <span className="avatar" aria-hidden="true">{b.photoUrl ? <img src={b.photoUrl} alt="" /> : initials(b.name)}</span>
                  <div className="card-title">ช่าง{b.name}</div>
                </div>
              </Card>
            ))}
            {barbersForService.length === 0 && <Empty title="ยังไม่มีช่างที่รับบริการนี้" action={call} />}
          </section>
        )}

        {step === 'date' && (
          <section aria-label="เลือกวัน">
            <h2 style={{ marginBottom: 12 }}>เลือกวัน</h2>
            {dates === null ? <Loading /> : dates.some((d) => d.state === 'available') ? (
              <div className="date-strip">
                {dates.map((d) => {
                  const p = dayParts(d.date);
                  const tag = d.state === 'closed' ? 'ปิด' : d.state === 'full' ? 'เต็ม' : '';
                  return (
                    <button key={d.date} type="button" className={`date-cell${d.date === date ? ' selected' : ''}`}
                      disabled={d.state !== 'available'} onClick={() => chooseDate(d.date)} aria-label={`${thaiDate(d.date)}${tag ? ` ${tag}` : ''}`}>
                      <span className="dow">{p.dow}</span><span className="day">{p.day}</span><span className="tag">{tag || ' '}</span>
                    </button>
                  );
                })}
              </div>
            ) : <Empty title="คิวเต็ม กรุณาโทรหาร้าน" text={`ในช่วง ${shop.rules.bookingWindowDays} วันข้างหน้า`} action={call} />}
          </section>
        )}

        {step === 'time' && date && (
          <section aria-label="เลือกเวลา">
            <h2>เลือกเวลา</h2>
            <p className="muted small" style={{ marginBottom: 12 }}>{thaiDate(date)}</p>
            {times === null ? <Loading /> : times.length > 0 ? (
              <div className="time-grid">
                {times.map((t) => (
                  <button key={t.start} type="button" className={`time-cell${slot?.start === t.start ? ' selected' : ''}`} onClick={() => chooseTime(t)}>
                    {timeOf(t.start)}
                  </button>
                ))}
              </div>
            ) : <Empty title="วันนี้คิวเต็มแล้ว" text="ลองเลือกวันอื่น" action={{ label: 'เลือกวันอื่น', onClick: () => setStep('date') }} />}
          </section>
        )}

        {step === 'review' && service && slot && date && (
          <section aria-label="ตรวจสอบการจอง">
            <h2 style={{ marginBottom: 12 }}>ตรวจสอบการจอง</h2>
            <Card>
              <dl className="summary">
                <dt>บริการ</dt><dd>{service.name} · {service.price.toLocaleString('th-TH')} บาท</dd>
                <dt>ช่าง</dt><dd>{barberId ? `ช่าง${barberName(barberId)}` : 'ช่างคนไหนก็ได้ (ระบบเลือกให้)'}</dd>
                <dt>วัน</dt><dd>{thaiDate(date)}</dd>
                <dt>เวลา</dt><dd>{timeOf(slot.start)} น.</dd>
              </dl>
            </Card>
            {!reschedule && (
              <div style={{ marginTop: 16 }}>
                <Field label="หมายเหตุถึงช่าง (ถ้ามี)" hint="ไม่เกิน 200 ตัวอักษร">
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} />
                </Field>
              </div>
            )}
            <p className="muted small" style={{ margin: '8px 0 16px' }}>
              ยกเลิกหรือเลื่อนได้ก่อนเวลานัด {Math.round((shop.rules.cancelCutoffMinutes / 60) * 10) / 10} ชม.
            </p>
            <Button block loading={submitting} onClick={submit}>{reschedule ? 'ยืนยันเลื่อนนัด' : 'ยืนยันการจอง'}</Button>
          </section>
        )}
      </Page>
      <CustomerTabs />
    </>
  );
}
